import { AlertCircle, Loader2, Mail, MessageSquare } from "lucide-react";
import { useRef, useState, useEffect } from "react";

interface VerificationCodeProps {
  onSubmit: (code: string) => void;
  onResendCode: (preferredLoginType?: "email" | "sms") => void;
  smsHint?: string;
  emailHint?: string;
  error?: string;
  isLoading?: boolean;
  verificationType?: "email" | "sms";
  smsLoginSupported?: boolean;
}

export function VerificationCodeVariantB({
  onSubmit,
  onResendCode,
  smsHint = "",
  emailHint = "",
  error,
  verificationType = "email",
  smsLoginSupported = false,
  isLoading = false,
}: VerificationCodeProps) {
  const [code, setCode] = useState("");
  const [currentVerificationType, setCurrentVerificationType] = useState<
    "email" | "sms"
  >(verificationType);
  const [currentSmsLoginSupported, _] = useState<boolean>(smsLoginSupported);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const clearCode = () => {
    setCode("");
    // Focus the first input after clearing
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  };

  // Auto-focus first input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle input change
  const handleChange = (value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    setCode(value);

    // If all digits are filled, submit the code
    if (value.length === 6) { 
      onSubmit(value);
    }
  };


  return (
    <div className="bg-white shadow-xl rounded-2xl overflow-hidden max-w-lg mx-auto">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
          Enter the code sent to
        </h3>
        <div className="flex items-center justify-center mb-6">
          <div className="rounded-full px-4 py-3 flex items-center gap-2">
            {currentVerificationType === "sms" ? (
              <>
                <MessageSquare className="w-6 h-6 text-primary-600" />{" "}
                <span className="text-primary-700 font-medium">
                  {smsHint}
                </span>
              </>
            ) : (
              <>
                <Mail className="w-6 h-6 text-primary-600" />
                <span className="text-primary-700 font-medium">
                  {emailHint}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="text-center space-y-3 mb-6">
          <div className="rounded-lg p-4">
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
              <p className="text-gray-700 mb-2">
                We found an existing account for{" "}
                <span className="font-medium text-gray-900">{emailHint}</span>{" "}
                and we need to verify you are the owner of the account
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-2 mb-6">
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e)=>handleChange(e.target.value)}
              placeholder="000000"
              data-1p-ignore
              className={`w-36 h-12 text-2xl font-semibold border rounded-lg 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                transition-all duration-200 tracking-widest placeholder:text-gray-200 pl-5 relative verification-code-input
                ${error ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-200"}`}
              style={{ 
                appearance: "textfield",
                backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQ4IiB2aWV3Qm94PSIwIDAgMTIwIDQ4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjx0ZXh0IHg9IjIwIiB5PSIzMyIgZm9udC1mYW1pbHk9InN5c3RlbS11aSwgLWFwcGxlLXN5c3RlbSwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZm9udC13ZWlnaHQ9IjYwMCIgZmlsbD0iI2Y1ZjZmNyIgbGV0dGVyLXNwYWNpbmc9IjAuMDNlbSI+MDAwMDAwPC90ZXh0Pjwvc3ZnPg==')",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0px center"
              }}
              aria-label={`Verification code`}
            />
        </div>

          <div className="text-red-500 text-sm mb-4 h-6 flex items-center justify-center">
          {error && code.length>=6 && (<>
            <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
            <span>{error}</span></>
          )}
          </div>

        <div className="text-center space-y-4">
          <button
            type="button"
            onClick={() => onSubmit(code)}
            disabled={code.length < 6 || isLoading}
            className="w-full bg-primary-600 text-white rounded-lg px-6 py-3 font-semibold text-lg
              hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
              focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
              transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Continue
          </button>

          <div className="flex flex-col space-y-2 mt-6">
            <button
              type="button"
              onClick={() => {
                clearCode();
                onResendCode(currentVerificationType);
              }}
              disabled={isLoading}
              className="text-gray-600 hover:text-gray-700 font-medium 
                focus:outline-none focus:ring-2 focus:ring-primary-500 
                focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend Code"
              )}
            </button>
            {currentVerificationType === "sms" ? (
              <button
                type="button"
                onClick={() => {
                  clearCode();
                  setCurrentVerificationType("email");
                  onResendCode("email");
                }}
                className="text-primary-600 hover:text-primary-700 font-medium 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 
                  focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                  transition-colors duration-200"
              >
                Use Email Instead
              </button>
            ) : currentSmsLoginSupported ? (
              <button
                type="button"
                onClick={() => {
                  clearCode();
                  setCurrentVerificationType("sms");
                  onResendCode("sms");
                }}
                className="text-primary-600 hover:text-primary-700 font-medium 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 
                  focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                  transition-colors duration-200"
              >
                Use SMS Instead
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
